import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Image as ImageIcon, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    description: ''
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gallery`, formData);
      toast.success('Image added to gallery successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add image');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}`);
      toast.success('Image deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image_url: '',
      description: ''
    });
  };

  return (
    <div data-testid="gallery-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase">Gallery Management</h2>
          <p className="text-slate-600 text-sm mt-1">Manage product images, banners, and promotional content</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-image-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Image
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Image</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Solar Panel Installation"
                  data-testid="image-title-input"
                />
              </div>
              <div>
                <Label>Image URL *</Label>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  placeholder="https://example.com/image.jpg"
                  data-testid="image-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the full URL of the image (Unsplash, Imgur, etc.)
                </p>
              </div>
              <div>
                <Label>Description (Optional)</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the image"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  data-testid="image-description-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-image-button">
                  Add to Gallery
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {images.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg mb-2">No images in gallery yet</p>
          <p className="text-slate-500 text-sm">Click "Add Image" to upload your first image</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map(image => (
            <Card key={image.id} className="group overflow-hidden border-slate-200 hover:shadow-xl transition-all duration-300" data-testid={`gallery-item-${image.id}`}>
              <div className="aspect-square relative overflow-hidden bg-slate-100">
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <a 
                      href={image.image_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white text-sm flex items-center gap-1 hover:underline"
                    >
                      View Full Size <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 truncate" title={image.title}>
                  {image.title}
                </h3>
                {image.description && (
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{image.description}</p>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">
                    {new Date(image.created_at).toLocaleDateString()}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(image.id)}
                    data-testid={`delete-image-${image.id}`}
                    className="hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="font-semibold mb-2">💡 Tips for Adding Images:</h3>
        <ul className="space-y-1 text-sm text-slate-700">
          <li>• Use high-quality images (minimum 800x800px recommended)</li>
          <li>• Supported formats: JPG, PNG, WebP</li>
          <li>• Free image sources: Unsplash, Pexels, Pixabay</li>
          <li>• Right-click on any image → Copy image address → Paste URL</li>
          <li>• Gallery images can be used for products, banners, or promotional content</li>
        </ul>
      </div>
    </div>
  );
};

export default Gallery;
